import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Toggle from 'react-toggle';
import _ from 'lodash';
import { getRsvpPageEvents } from '../../../actions/website/entities/RsvpActions';
import DragHandle from '../../common/ui/DragHandle';
import { togglePage, movePage } from '../../../actions/website/PageActions';
import { showModal, hideModal } from '../../../actions/ModalActions';

const Page = SortableElement(({ onToggle, page, registryPublished, closeModal }) => {
  const noPropagate = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  const navigateToPage = (e) => {
    noPropagate(e);
    const pageType = page.type.toLowerCase();
    browserHistory.push(`/wedding/manage/website/pages/${pageType}`);
    closeModal();
  };

  const disabled = page.type === 'HOME';
  return (
    <li className={`manage-page sortable-item ${page.hidden ? 'not-shown' : 'visible'} ${page.type.toLowerCase()}`} style={{ zIndex: 1100 }}>
      <div className="edit-link" onClick={navigateToPage}>
        <div className="col-xs-1 sortable-item-col" onClick={noPropagate}>
          <DragHandle disabled={disabled} tooltip="Drag to reorder" />
        </div>
        <div className="col-xs-8 sortable-item-col">
          <i className={`zolaicon zolaicon-${page.type.toLowerCase()}`} />
          <div className="title">
            {page.menu_title}
            {page.type === 'REGISTRY' && (
              <span className="subtitle text-small">Zola Registry is {registryPublished ? 'visible' : 'hidden'}</span>
            )}
          </div>
        </div>
        <div className="col-xs-3" onClick={noPropagate}>
          {!disabled && (
            <div>
              <div className="with-tooltip no-tooltip-on-mobile" data-tooltip="Control if guests see this page. Set to &quot;Hidden&quot; if you don't want to use it or it's still a work in progress. Set to &quot;Visible&quot; if you're ready to share.">
                <Toggle
                  checked={!page.hidden}
                  onChange={onToggle}
                  icons={{ checked: <span className="toggle-label checked">Visible</span>, unchecked: <span className="toggle-label unchecked">Hidden</span> }}
                />
              </div>
            </div>
          )}
          {disabled && <em className="text-muted must-be-visible">Must be visible</em>}
        </div>
      </div>
    </li>
  );
});

Page.propTypes = {
  onToggle: PropTypes.func,
  page: PropTypes.shape({}),
  registryPublished: PropTypes.bool,
  closeModal: PropTypes.func,
};

const PageList = SortableContainer(({ pages, orderedIds, onToggle, registryPublished, closeModal }) => (
  <ul className="sortable-list">
    {orderedIds.map((pageId, idx) => (
      <Page
        key={idx}
        index={idx}
        onToggle={() => onToggle(pageId, pages[pageId])}
        page={pages[pageId]}
        registryPublished={registryPublished}
        closeModal={closeModal}
      />
    ))}
  </ul>
));

PageList.propTypes = {
  pages: PropTypes.shape({}),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  onToggle: PropTypes.func,
  onSortEnd: PropTypes.func,
  registryPublished: PropTypes.bool,
  closeModal: PropTypes.func,
};

class ReorderPagesModal extends Component {
  constructor(props) {
    super(props);

    this.handlePageToggle = this.handlePageToggle.bind(this);
  }

  componentDidMount() {
    const { getRsvpEntities } = this.props;
    getRsvpEntities();
  }

  handlePageToggle(pageId, currentPage) {
    const currentPageType = currentPage.type;
    const { page, guests, rsvpEntities, orderedIds, onPageToggle, showErrorModal, showSuccessModal } = this.props;
    const numGuestsInvited = _.reduce(guests.orderedIds, (sum, guestGroupId) => {
      const guestGroup = guests.byId[guestGroupId];
      const eventInvitations = _.flatMap(guestGroup.guests, guest => guest.event_invitations);
      return sum + eventInvitations.length;
    }, 0);
    const noEvents = orderedIds.length === 0;
    const currentPageTypeIsRsvp = currentPageType === 'RSVP';
    const noGuests = _.isEmpty(guests.byId);
    const noGuestsInvited = numGuestsInvited === 0;
    const rsvpDisabled = orderedIds.filter(id => rsvpEntities[id].collect_rsvps === true).length === 0;

    if (!page.hidden) {
      onPageToggle(pageId);
    } else if (currentPageTypeIsRsvp && noEvents) {
      const errorModalProps = {
        heading: 'Want guests to RSVP on your wedding website?',
        body: 'First, create an event and build a guest list for it. Then you\'ll be able to unhide this page and let guests RSVP on this page',
        button: {
          text: 'Create An Event',
          action: () => browserHistory.push('/wedding/manage/events')
        }
      };
      showErrorModal(errorModalProps);
    } else if (currentPageTypeIsRsvp && noGuests) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, build your Guest Directory.',
        button: {
          text: 'Go To Guest Directory',
          action: () => browserHistory.push('/wedding/manage/guests/all')
        }
      };
      showErrorModal(errorModalProps);
    } else if (currentPageTypeIsRsvp && noGuestsInvited) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, choose some to invite.',
        button: {
          text: 'Invite Guests',
          action: () => browserHistory.push('/wedding/manage/guests/build')
        }
      };
      showErrorModal(errorModalProps);
    } else if (currentPageTypeIsRsvp && rsvpDisabled) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'Turn on online RSVPs for at least one of your events (and make sure guests have already been invited).',
      };
      showErrorModal(errorModalProps);
    } else if (currentPageTypeIsRsvp && page.hidden) {
      showSuccessModal();
      onPageToggle(pageId);
    } else {
      onPageToggle(pageId);
    }
  }

  render() {
    const { pages, onSortEnd, registry, closeModal } = this.props;
    return (
      <div id="reorder-pages-modal">
        <div className="modal-header">
          <h3>Reorder Pages</h3>
        </div>
        <div className="modal-body">
          <p className="instructions text-muted">
            Drag and drop to change how your pages appear on your website&#39;s navigation bar.
          </p>
          <div className="sortable-pages">
            <PageList
              pages={pages.byId}
              orderedIds={pages.orderedIds}
              onToggle={this.handlePageToggle}
              onSortEnd={onSortEnd}
              useDragHandle
              lockAxis="y"
              registryPublished={registry.is_public}
              closeModal={closeModal}
            />
          </div>
        </div>
      </div>
    );
  }
}

ReorderPagesModal.propTypes = {
  onSortEnd: PropTypes.func,
  registry: PropTypes.shape({
    is_public: PropTypes.bool
  }),
  showSuccessModal: PropTypes.func,
  showErrorModal: PropTypes.func,
  closeModal: PropTypes.func,
  page: PropTypes.shape({
    hidden: PropTypes.bool
  }),
  pages: PropTypes.shape({
    byId: PropTypes.array,
    orderedIds: PropTypes.array,
  }),
  guests: PropTypes.shape({
    byId: PropTypes.array,
    orderedIds: PropTypes.arrayOf(PropTypes.number)
  }),
  rsvpEntities: PropTypes.shape({}),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  onPageToggle: PropTypes.func,
  getRsvpEntities: PropTypes.func,
};

const mapStateToProps = state => ({
  pages: state.website.pages,
  guestGroups: state.guestList.guests.byId,
  registry: state.registry,
  events: state.events.events.orderedIds,
  rsvpEntities: state.website.entities.byId,
  orderedIds: state.website.entities.orderedIds,
  page: state.website.pages.byId[state.website.pages.typeToId.RSVP],
  guests: state.guestList.guests,
});

const mapDispatchToProps = dispatch => ({
  showErrorModal: errorModalProps => dispatch(showModal('PUBLISH_PAGE_ERROR', { ...errorModalProps }, { size: 'md' })),
  showSuccessModal: () => dispatch(showModal('PUBLISH_RSVP', {}, { size: 'md' })),
  onSortEnd: ({ oldIndex, newIndex }) => {
    if (oldIndex > 0 && newIndex > 0) {
      dispatch(movePage(oldIndex, newIndex));
    }
  },
  closeModal: () => dispatch(hideModal()),
  getRsvpEntities: () => dispatch(getRsvpPageEvents()),
  onPageToggle: pageId => dispatch(togglePage(pageId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReorderPagesModal);



// WEBPACK FOOTER //
// ./src/components/manage/website/ReorderPagesModal.jsx